import { IconNames } from '@blueprintjs/icons';
import { sortAsc } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { RequestType } from '@strata/react-chat';
import { Checkbox } from '@strata/react-checkbox-list';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { FormAccordion } from '@strata/react-form-section';
import { InlineMessage } from '@strata/react-inline-message';
import { TextInputFormik } from '@strata/react-inputs';
import { PageTitle } from '@strata/react-page-title';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import classNames from 'classnames';
import { Formik, useFormikContext } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { useAppSelector } from 'store';
import {
    type PayerNotificationPreferences,
    useGetPayerNotificationPreferencesQuery,
    usePostUpdatePayerNotificationPreferencesMutation,
} from 'store/api/notificationApi';
import { useGetPayerDetailsQuery, useGetSegmentsQuery } from 'store/api/referenceApi';

const RequestCreatedTypes = {
    [RequestType.ESCALATION]: 'Member Inquiry Requests',
    [RequestType.CHANGE]: 'Change Management Requests',
    [RequestType.OTHER]: 'Other Requests',
} as const;

interface ChecboxAndEmailProps {
    segmentId: string;
    type: string;
    label: string;
    typeIndex: number;
}

const CheckboxAndEmail = ({ segmentId, type, label, typeIndex }: ChecboxAndEmailProps) => {
    const formik = useFormikContext<PayerNotificationPreferences>();

    const isChecked = formik.values?.[segmentId]?.[type] !== undefined;
    return (
        <>
            <Checkbox
                name={`${segmentId}.${typeIndex}`}
                checked={isChecked}
                label={label}
                inline
                onChange={(checked) => {
                    void formik.setFieldValue(`${segmentId}.${type}`, checked ? null : undefined);
                }}
            />
            {isChecked && (
                <div
                    className="padding__left--large"
                    style={{
                        width: '250px',
                    }}
                >
                    <TextInputFormik
                        id={`${segmentId}.${type}`}
                        label="Email Address"
                        required
                        onChange={(e) => {
                            void formik.setFieldValue(
                                `${segmentId}.${type}`,
                                e.target.value === '' ? null : e.target.value,
                            );
                        }}
                    />
                </div>
            )}
        </>
    );
};

export const TeamNotificationsPage = () => {
    const { payerId } = useAppSelector((state) => state.auth);
    const { data: payerDetails } = useGetPayerDetailsQuery({ payerId: payerId! });
    const { data: segments } = useGetSegmentsQuery({ payerId: payerId! });
    const { data: configuredNotifications } = useGetPayerNotificationPreferencesQuery();
    const [updatePayerNotificationPreferences] =
        usePostUpdatePayerNotificationPreferencesMutation();
    const [openSection, setOpenSection] = useState<number>(0);

    return (
        <FormPageContainer>
            <Formik
                initialValues={configuredNotifications ?? {}}
                validationSchema={Yup.object({}).shape(
                    Object.fromEntries(
                        Object.entries(configuredNotifications ?? {}).map(([segment_id]) => [
                            segment_id,
                            Yup.object().shape(
                                Object.fromEntries(
                                    Object.entries(RequestCreatedTypes).map(([type]) => [
                                        type,
                                        Yup.string()
                                            .optional()
                                            .nonNullable(
                                                validationStrings.requiredField.errorMessage,
                                            )
                                            .email(validationStrings.email.errorMessage),
                                    ]),
                                ),
                            ),
                        ]),
                    ),
                )}
                onSubmit={async (values) => {
                    await updatePayerNotificationPreferences(values).unwrap();
                    SuccessToast({ message: 'Team notifications updated successfully.' });
                }}
                enableReinitialize
                validateOnMount
            >
                {(formik) => (
                    <div className="margin__around--medium">
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <PageTitle
                                title="Team Notifications"
                                secondaryTitle={payerDetails?.payerName}
                                className="padding__bottom--large"
                            />
                            <Button
                                intent={Button.Intent.PRIMARY}
                                label="Save"
                                id="team-notifications-save-button"
                                onClick={() => formik.handleSubmit()}
                                disabled={formik.isSubmitting || !formik.dirty || !formik.isValid}
                            />
                        </div>
                        <div className="sds_team_notifications__header">
                            New Request Email Notifications
                        </div>
                        <div className="flex__gap--medium">
                            <div style={{ flex: 1 }}>
                                <FormAccordion
                                    sectionDataArray={Object.keys(formik.values ?? {})
                                        .map((segment_id) => [
                                            segments?.segmentsMap?.[segment_id] ??
                                                'Unknown Business Segment',
                                            segment_id,
                                        ])
                                        .sort(([segment_name_a], [segment_name_b]) =>
                                            sortAsc(segment_name_a, segment_name_b),
                                        )
                                        .map(([segment_name, segment_id], index) => ({
                                            sectionTitle: (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    {segment_name}
                                                    <Icon
                                                        icon={IconNames.CHEVRON_DOWN}
                                                        className={classNames(
                                                            'sds_team_notifications__chevron',
                                                            {
                                                                'sds_team_notifications__chevron--up':
                                                                    openSection === index,
                                                            },
                                                        )}
                                                        intent={Icon.Intent.DEFAULT}
                                                        onClick={() => setOpenSection(index)}
                                                        size={24}
                                                    />
                                                </div>
                                            ),
                                            displayFooter: false,
                                            scrollToTopOnOpen: true,
                                            isOpen: openSection === index,
                                            disabled: false,
                                            onSectionClick: () => setOpenSection(index),
                                            content: (
                                                <div
                                                    className="flex__gap--medium"
                                                    style={{ flexDirection: 'column' }}
                                                >
                                                    {Object.entries(RequestCreatedTypes).map(
                                                        ([type, label], typeIndex) => (
                                                            <CheckboxAndEmail
                                                                key={`${segment_id}.${type}`}
                                                                segmentId={segment_id}
                                                                type={type}
                                                                label={label}
                                                                typeIndex={typeIndex}
                                                            />
                                                        ),
                                                    )}
                                                </div>
                                            ),
                                        }))}
                                />
                            </div>
                            <div
                                className="flex__gap--medium"
                                style={{ flexDirection: 'column', width: '300px' }}
                            >
                                <InlineMessage
                                    title="Configure settings per payer"
                                    label="Admin settings are configured for the currently selected payer and will affect all users. To edit a different payer's settings, you can use the Switch Payer option within your profile dropdown."
                                    intent={InlineMessage.Intent.INFO}
                                    wrapText
                                />
                                <InlineMessage
                                    title="Using Team Email Notifications"
                                    label="Team notifications help streamline the assignment process by sending an email notification to a shared email address when a request is submitted."
                                    intent={InlineMessage.Intent.TIP}
                                    wrapText
                                />
                            </div>
                        </div>
                    </div>
                )}
            </Formik>
        </FormPageContainer>
    );
};
